import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { errorMessage } from "@/lib/errorMessage";
import type {
  AccountErasureRequestResponse,
  AccountErasureScheduledResponse,
  CreateAccountErasureRequest,
  GuestLookupResponse,
} from "@/types/generated/cms.generated";

/**
 * Support account deletion — GUESTS ONLY (CMS API `account-erasures`, AthlynAdmin + AthlynTeam).
 * Employees are removed by their organisation's HR, never here: the API answers an employee email
 * with `NOT_A_GUEST` and refuses anything but a `FullAccount` erasure.
 */

const ERASURES_KEY = ["account-erasures", "support"] as const;

/** Rows support may see: full-account erasures a guest asked for (Self) or support raised. */
export function isSupportListedErasure(row: AccountErasureRequestResponse): boolean {
  return row.scope === "FullAccount" && (row.trigger === "Self" || row.trigger === "AthlynSupport");
}

/** Looks a guest up by email. Runs only once an email has been submitted. */
export function useGuestLookup(email: string) {
  const trimmed = email.trim();
  return useQuery<GuestLookupResponse>({
    queryKey: ["account-erasures", "guest-lookup", trimmed],
    queryFn: () =>
      apiRequest<GuestLookupResponse>(
        "GET",
        `/account-erasures/guest-lookup?email=${encodeURIComponent(trimmed)}`,
      ),
    enabled: trimmed.length > 0,
    retry: false,
  });
}

/** Full-account erasures filtered to Self / AthlynSupport (never employee or inactivity rows). */
export function useSupportAccountErasures() {
  return useQuery<AccountErasureRequestResponse[]>({
    queryKey: ERASURES_KEY,
    queryFn: async () => {
      const rows = await apiRequest<AccountErasureRequestResponse[]>(
        "GET",
        "/account-erasures?scope=FullAccount",
      );
      return (rows ?? []).filter(isSupportListedErasure);
    },
  });
}

/** Schedules a guest's full-account erasure (signed out now, erased within 30 days). */
export function useCreateGuestErasure() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => {
      const body: CreateAccountErasureRequest = { userId, scope: "FullAccount" };
      return apiRequest<AccountErasureScheduledResponse>("POST", "/account-erasures", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-erasures"] });
      toast({ title: "Account deletion scheduled", description: "The guest has been signed out." });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not delete the account",
        description: errorMessage(error, "Please try again."),
        variant: "destructive",
      });
    },
  });
}

/** Cancels a scheduled erasure. */
export function useCancelAccountErasure() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (requestId: string) =>
      apiRequest<boolean>("POST", `/account-erasures/${requestId}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-erasures"] });
      toast({ title: "Deletion cancelled", description: "The account will not be deleted." });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not cancel the deletion",
        description: errorMessage(error, "Please try again."),
        variant: "destructive",
      });
    },
  });
}
